import { FormattedMessage } from '@flipster/i18n';
import { QuoteCurrencyListExceptUsdt } from '@flipster/utils/constants/currency';
import { mapToObj } from '@flipster/utils/map-to-obj';

import { DesktopTabInfo, MobileTabInfo, MultiHeaderItem, SingleHeaderItem, TabValueType } from './types';

export const MarketTabHeightVariableKey = '--market-tab-height';
export const MarketDiscoverTabHeightVariableKey = '--market-discover-tab-height';

interface TabInfoOption {
  width?: number;
  sort?: boolean;
  align?: 'left' | 'right';
  fullWidth?: boolean;
}
const tabInfo = {
  // only desktop
  symbolWithLeverage(opt?: TabInfoOption) {
    return {
      key: 'symbol',
      title: <FormattedMessage id='Common.r250923.Name' />,
      align: 'left',
      ...opt,
      sortKey: undefined,
      subColumns: [
        {
          key: 'symbol',
          title: <FormattedMessage id='Common.r250923.Name' />,
          sortKey: opt?.sort ? 'symbol' : undefined,
        },
        {
          key: 'leverage',
          title: <FormattedMessage id='Assets.r250401.Leverage' />,
          sortKey: opt?.sort ? 'leverage' : undefined,
        },
      ],
    } satisfies SingleHeaderItem;
  },
  // only mobile
  symbol(opt?: TabInfoOption) {
    const key = 'symbol';
    return {
      key,
      title: <FormattedMessage id='Common.r250923.Name' />,
      align: 'left',
      ...opt,
      sortKey: opt?.sort ? key : undefined,
    } satisfies SingleHeaderItem;
  },
  turnover24h(opt?: TabInfoOption) {
    const key = 'turnover24h';
    return {
      key,
      title: <FormattedMessage id='Common.r250923.Hour_h_volume' values={{ hour: 24 }} />,
      align: 'right',
      ...opt,
      sortKey: opt?.sort ? key : undefined,
    } satisfies SingleHeaderItem;
  },
  displayPrice(opt?: TabInfoOption) {
    const key = 'displayPrice';
    return {
      key,
      title: <FormattedMessage id='Common.r250923.Price' />,
      align: 'right',
      ...opt,
      sortKey: opt?.sort ? key : undefined,
    } satisfies SingleHeaderItem;
  },
  fundingRate(opt?: TabInfoOption) {
    const key = 'fundingRate';
    return {
      key,
      title: <FormattedMessage id='Market.r250923.Funding' />,
      align: 'right',
      ...opt,
      sortKey: opt?.sort ? key : undefined,
    } satisfies SingleHeaderItem;
  },
  leverage(opt?: TabInfoOption) {
    const key = 'leverage';
    return {
      key,
      title: <FormattedMessage id='Assets.r250401.Leverage' />,
      align: 'left',
      ...opt,
      sortKey: opt?.sort ? key : undefined,
    } satisfies SingleHeaderItem;
  },
  priceChangePct24h(opt?: TabInfoOption) {
    const key = 'priceChangePct24h';
    return {
      key,
      title: <FormattedMessage id='Common.r250923.Percent_change' />,
      align: 'right',
      ...opt,
      sortKey: opt?.sort ? key : undefined,
    } satisfies SingleHeaderItem;
  },
  action(opt?: TabInfoOption) {
    const key = 'action';
    return {
      key: 'action',
      title: null,
      align: 'right',
      ...opt,
      sortKey: opt?.sort ? key : undefined,
    } satisfies SingleHeaderItem;
  },
  displayPriceWithPriceChangePct24h(opt?: TabInfoOption) {
    const { sort, ...optWithoutSort } = opt ?? {};
    return {
      items: [
        {
          key: 'displayPrice',
          title: (
            <>
              <FormattedMessage id='Common.r250923.Price' />/
            </>
          ),
        },
        {
          key: 'priceChangePct24h',
          title: <FormattedMessage id='Common.r250923.Hour_h_change' values={{ hour: 24 }} />,
          sortKey: sort ? 'priceChangePct24h' : undefined,
        },
      ],
      align: 'right',
      ...optWithoutSort,
    } satisfies MultiHeaderItem;
  },
};

const quoteCurrencyMapper: {
  [type in 'perpetual' | 'spot']: {
    desktop: Record<(typeof QuoteCurrencyListExceptUsdt)[number], DesktopTabInfo>;
    mobile: Record<(typeof QuoteCurrencyListExceptUsdt)[number], MobileTabInfo>;
  };
} = {
  perpetual: {
    desktop: mapToObj(
      QuoteCurrencyListExceptUsdt,
      () =>
        [
          tabInfo.symbolWithLeverage({ sort: true }),
          tabInfo.turnover24h({ sort: true, width: 176 }),
          tabInfo.displayPrice({ sort: true, width: 176 }),
          tabInfo.priceChangePct24h({ sort: true, width: 156 }),
          tabInfo.action({ width: 144 }),
        ] satisfies DesktopTabInfo,
    ),
    mobile: mapToObj(
      QuoteCurrencyListExceptUsdt,
      () =>
        [
          tabInfo.symbol({ sort: true, fullWidth: true }),
          tabInfo.displayPriceWithPriceChangePct24h({ sort: true, width: 128 }),
        ] satisfies MobileTabInfo,
    ),
  },
  spot: {
    desktop: mapToObj(QuoteCurrencyListExceptUsdt, () => []),
    mobile: mapToObj(QuoteCurrencyListExceptUsdt, () => []),
  },
};
export const desktopTabInfoMapper: Record<TabValueType, DesktopTabInfo> = {
  favorites: [
    tabInfo.symbolWithLeverage({ sort: true }),
    tabInfo.turnover24h({ sort: true, width: 176 }),
    tabInfo.displayPrice({ sort: true, width: 176 }),
    tabInfo.priceChangePct24h({ sort: true, width: 156 }),
    tabInfo.action({ width: 144 }),
  ],
  marketCap: [
    tabInfo.symbolWithLeverage({ sort: true }),
    tabInfo.turnover24h({ sort: true, width: 176 }),
    tabInfo.displayPrice({ sort: true, width: 176 }),
    tabInfo.priceChangePct24h({ sort: true, width: 156 }),
    tabInfo.action({ width: 144 }),
  ],
  newListings: [
    tabInfo.symbolWithLeverage({ sort: true }),
    tabInfo.turnover24h({ sort: true, width: 176 }),
    tabInfo.displayPrice({ sort: true, width: 176 }),
    tabInfo.priceChangePct24h({ sort: true, width: 156 }),
    tabInfo.action({ width: 144 }),
  ],
  priceChange24h: [
    tabInfo.symbolWithLeverage(),
    tabInfo.turnover24h({ width: 176 }),
    tabInfo.displayPrice({ width: 176 }),
    tabInfo.priceChangePct24h({ sort: true, width: 156 }),
    tabInfo.action({ width: 144 }),
  ],
  volume: [
    tabInfo.symbolWithLeverage(),
    tabInfo.turnover24h({ width: 176 }),
    tabInfo.displayPrice({ width: 176 }),
    tabInfo.priceChangePct24h({ width: 156 }),
    tabInfo.action({ width: 144 }),
  ],
  absFundingRates: [
    tabInfo.symbolWithLeverage(),
    tabInfo.fundingRate({ width: 176 }),
    tabInfo.turnover24h({ width: 176 }),
    tabInfo.displayPrice({ width: 176 }),
    tabInfo.priceChangePct24h({ width: 156 }),
    tabInfo.action({ width: 144 }),
  ],
  yourFrequentTrades: [
    tabInfo.symbolWithLeverage({ sort: true }),
    tabInfo.turnover24h({ sort: true, width: 176 }),
    tabInfo.displayPrice({ sort: true, width: 176 }),
    tabInfo.priceChangePct24h({ sort: true, width: 156 }),
    tabInfo.action({ width: 144 }),
  ],
  hotTradings: [
    tabInfo.symbolWithLeverage({ sort: true }),
    tabInfo.turnover24h({ sort: true, width: 176 }),
    tabInfo.displayPrice({ sort: true, width: 176 }),
    tabInfo.priceChangePct24h({ sort: true, width: 156 }),
    tabInfo.action({ width: 144 }),
  ],
  tradfi: [
    tabInfo.symbolWithLeverage({ sort: true }),
    tabInfo.turnover24h({ sort: true, width: 176 }),
    tabInfo.displayPrice({ sort: true, width: 176 }),
    tabInfo.priceChangePct24h({ sort: true, width: 156 }),
    tabInfo.action({ width: 144 }),
  ],
  ...quoteCurrencyMapper.perpetual.desktop,
};

export const mobileTabInfoMapper: Record<TabValueType, MobileTabInfo> = {
  favorites: [
    tabInfo.symbol({ sort: true, fullWidth: true }),
    tabInfo.displayPriceWithPriceChangePct24h({ sort: true, width: 128 }),
  ],
  marketCap: [
    tabInfo.symbol({ sort: true, fullWidth: true }),
    tabInfo.displayPriceWithPriceChangePct24h({ sort: true, width: 128 }),
  ],
  newListings: [
    tabInfo.symbol({ sort: true, fullWidth: true }),
    tabInfo.displayPriceWithPriceChangePct24h({ sort: true, width: 128 }),
  ],
  priceChange24h: [
    tabInfo.symbol({ align: 'left', fullWidth: true }),
    tabInfo.displayPriceWithPriceChangePct24h({ sort: true, width: 128 }),
  ],
  volume: [
    tabInfo.symbol({ align: 'left', fullWidth: true }),
    tabInfo.turnover24h({ width: 90 }),
    tabInfo.displayPriceWithPriceChangePct24h({ width: 100 }),
  ],
  absFundingRates: [
    tabInfo.symbol({ align: 'left', fullWidth: true }),
    tabInfo.fundingRate({ width: 90 }),
    tabInfo.displayPriceWithPriceChangePct24h({ width: 100 }),
  ],
  yourFrequentTrades: [
    tabInfo.symbol({ sort: true, fullWidth: true }),
    tabInfo.displayPriceWithPriceChangePct24h({ sort: true, width: 128 }),
  ],
  hotTradings: [
    tabInfo.symbol({ sort: true, fullWidth: true }),
    tabInfo.displayPriceWithPriceChangePct24h({ sort: true, width: 128 }),
  ],
  tradfi: [
    tabInfo.symbol({ sort: true, fullWidth: true }),
    tabInfo.displayPriceWithPriceChangePct24h({ sort: true, width: 128 }),
  ],
  ...quoteCurrencyMapper.perpetual.mobile,
};

export const spotDesktopTabInfoMapper: Record<TabValueType, DesktopTabInfo> = {
  favorites: [
    tabInfo.symbol({ sort: true }),
    tabInfo.turnover24h({ sort: true, width: 176 }),
    tabInfo.displayPrice({ sort: true, width: 176 }),
    tabInfo.priceChangePct24h({ sort: true, width: 156 }),
    tabInfo.action({ width: 144 }),
  ],
  marketCap: [
    tabInfo.symbol({ sort: true }),
    tabInfo.turnover24h({ sort: true, width: 176 }),
    tabInfo.displayPrice({ sort: true, width: 176 }),
    tabInfo.priceChangePct24h({ sort: true, width: 156 }),
    tabInfo.action({ width: 144 }),
  ],
  newListings: [
    tabInfo.symbol({ sort: true }),
    tabInfo.turnover24h({ sort: true, width: 176 }),
    tabInfo.displayPrice({ sort: true, width: 176 }),
    tabInfo.priceChangePct24h({ sort: true, width: 156 }),
    tabInfo.action({ width: 144 }),
  ],
  volume: [
    tabInfo.symbol(),
    tabInfo.turnover24h({ width: 176 }),
    tabInfo.displayPrice({ width: 176 }),
    tabInfo.priceChangePct24h({ width: 156 }),
    tabInfo.action({ width: 144 }),
  ],
  priceChange24h: [
    tabInfo.symbol(),
    tabInfo.turnover24h({ width: 176 }),
    tabInfo.displayPrice({ width: 176 }),
    tabInfo.priceChangePct24h({ sort: true, width: 156 }),
    tabInfo.action({ width: 144 }),
  ],
  absFundingRates: [],
  yourFrequentTrades: [
    tabInfo.symbol({ sort: true }),
    tabInfo.turnover24h({ sort: true, width: 176 }),
    tabInfo.displayPrice({ sort: true, width: 176 }),
    tabInfo.priceChangePct24h({ sort: true, width: 156 }),
    tabInfo.action({ width: 144 }),
  ],
  hotTradings: [], // Perpetual 탭에서만 사용
  tradfi: [], // Perpetual 탭에서만 사용
  ...quoteCurrencyMapper.spot.desktop,
};

export const spotMobileTabInfoMapper: Record<TabValueType, MobileTabInfo> = {
  favorites: [
    tabInfo.symbol({ sort: true, fullWidth: true }),
    tabInfo.displayPriceWithPriceChangePct24h({ sort: true, width: 128 }),
  ],
  marketCap: [
    tabInfo.symbol({ sort: true, fullWidth: true }),
    tabInfo.displayPriceWithPriceChangePct24h({ sort: true, width: 128 }),
  ],
  newListings: [
    tabInfo.symbol({ sort: true, fullWidth: true }),
    tabInfo.displayPriceWithPriceChangePct24h({ sort: true, width: 128 }),
  ],
  volume: [
    tabInfo.symbol({ align: 'left', fullWidth: true }),
    tabInfo.turnover24h({ width: 90 }),
    tabInfo.displayPriceWithPriceChangePct24h({ width: 100 }),
  ],
  priceChange24h: [
    tabInfo.symbol({ align: 'left', fullWidth: true }),
    tabInfo.displayPriceWithPriceChangePct24h({ sort: true, width: 128 }),
  ],
  absFundingRates: [],
  yourFrequentTrades: [
    tabInfo.symbol({ sort: true, fullWidth: true }),
    tabInfo.displayPriceWithPriceChangePct24h({ sort: true, width: 128 }),
  ],
  hotTradings: [], // Perpetual 탭에서만 사용
  tradfi: [], // Perpetual 탭에서만 사용
  ...quoteCurrencyMapper.spot.mobile,
};
